/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * The shape the API refuses in: RFC7807, where `title` is the domain
 * error's class name and `detail` the sentence it wrote.
 */
interface ProblemBody {
  title?: string;
  detail?: string;
}

/**
 * The i18n key each refusal reads as, keyed by the error class.
 *
 * Keyed on `title` rather than matched against `detail`: the message is
 * a sentence the backend may reword, and grouping refusals by spelling
 * is exactly how the lost-reason vocabulary went wrong before it was
 * closed. The class name is the contract.
 */
const KEY_BY_TITLE: Record<string, string> = {
  DealInvalidTransitionError: 'board.refusal.invalidTransition',
  DealPropertyRequiredError: 'board.refusal.propertyRequired',
  DealClientRequiredError: 'board.refusal.clientRequired',
  DealLostReasonValidationError: 'board.refusal.lostReason',
};

/**
 * A terminal attendance refuses every move, and says so in the same
 * error class as every other invalid transition. The board can tell the
 * two apart because it knows where the card came from, and "this one is
 * closed" is a different sentence from "it cannot go there yet".
 */
const TERMINAL_STAGES = new Set(['won', 'lost']);

/**
 * The sentence to show when the server refuses a move.
 *
 * Translated rather than passed through: the API answers in English and
 * names its own internals ("property_id required from proposal
 * onwards"), which is a sentence for whoever wrote the rule, not for the
 * broker who just dragged a card.
 *
 * @param error - Whatever the request threw.
 * @param fromStage - The stage the card was sitting in.
 * @param t - The `features/attendances` translator.
 * @returns A localized sentence.
 */
export function moveRefusalMessage(
  error: unknown,
  fromStage: string | undefined,
  t: (key: string) => string,
): string {
  if (fromStage && TERMINAL_STAGES.has(fromStage)) {
    return t('board.refusal.terminal');
  }
  const body = (error as { body?: ProblemBody } | undefined)?.body;
  const key = body?.title ? KEY_BY_TITLE[body.title] : undefined;
  return key ? t(key) : t('board.refusal.generic');
}
